import { KENNUNG_RE, configJsonPath, currentPathTenant, kennungFromPath } from './tenantPath'

/**
 * Laufzeit-Konfiguration des Cockpits (TQ #409, SPEC-Cockpit-Pro-Kunde §4.1).
 *
 * Ein Image fuer alle Kunden: Realm, Keycloak, API und Branding kommen aus
 * /config.json (beim Start geladen, per ConfigMap mountbar), nicht mehr aus
 * dem Hostnamen oder aus VITE_*-Werten. Die Datei wird VOR dem Keycloak-Init
 * geladen; die App rendert erst, wenn sie da und gueltig ist.
 *
 * Kunden-Instanz: flache Datei, keine `hostOverrides`.
 * Geteiltes Cockpit (Uebergang): die Standard-Config im Image traegt eine
 * `hostOverrides`-Tabelle (Daten, kein Code), die das heutige Verhalten je
 * Hostname nachbildet. BEFRISTETE AUSNAHME: sie entfaellt mit der Abschaltung des
 * geteilten Cockpits (Phase 9, SPEC-Cockpit-pro-Kunde); neue Kunden gehoeren dort nicht hinein.
 */

/**
 * - `shared`:   IT-SCE-Cockpit mit Org-Picker / Slug-Routen, Keycloak `check-sso`
 *               (cockpit.it-sce.de, org.it-sce.de, lokale Dev-Hosts)
 * - `tenant`:   Cockpit genau eines Kunden, Routen ohne Slug, Keycloak `login-required`
 * - `personal`: Privatnutzer-Cockpit (Personal-Users-Realm), Keycloak `check-sso`
 */
export type CockpitInstanceMode = 'shared' | 'tenant' | 'personal'

const MODES: readonly CockpitInstanceMode[] = ['shared', 'tenant', 'personal']

export interface RuntimeConfig {
  /** Keycloak-Realm. Fehlt er, wird er wie bisher per /api/public/realm-for-host aufgeloest. */
  realm?: string
  keycloakUrl: string
  clientId: string
  /** Basis-URL der API; leer = gleicher Origin (nginx-Proxy /api). */
  apiBase: string
  customerLabel: string
  mode: CockpitInstanceMode
  /** IT-SCE-Staff sieht hier zusaetzlich die interne Sektion (heute nur cockpit.it-sce.de). */
  staffCockpit: boolean
  logoUrl?: string
  primaryColor?: string
  /** Origin der Cortex-Instanz (Chat/Budget). Fehlt er, loest das geteilte Cockpit per cortex-for-host auf. */
  cortexUrl?: string
  /** Origin der admin-api (nur Staff-Cockpit, mode=shared). */
  adminApiBase?: string
  /** Tenant-Slug der EINEN Org dieser Instanz (cockpit-api: /api/org/<orgRef>/..., muss COCKPIT_TENANT_SLUG sein). */
  orgRef?: string
  /**
   * Kennung aus dem ersten Pfadsegment (Pfad-Instanz unter cockpit.it-sce.de/<kennung>/, TQ #409 E9).
   * Wird NICHT aus der Datei gelesen, sondern aus Pfad + <base href> (lib/tenantPath.ts).
   */
  tenantId?: string
  /**
   * Bereiche, die in dieser Instanz nur den Hinweis "Demnaechst verfuegbar" zeigen (Menuepunkt bleibt).
   * Erlaubt: aria, infra, downloads, settings (ganzer Bereich) oder settings:<tab> (organisation, soul, aria,
   * datenschutz, audit). Fehlt der Schluessel, aendert sich nichts (andere Cockpits unveraendert).
   */
  comingSoon?: string[]
}

export interface RuntimeConfigFile extends Partial<RuntimeConfig> {
  hostOverrides?: Record<string, Partial<RuntimeConfig>>
}

const DEFAULTS: RuntimeConfig = {
  keycloakUrl: 'https://auth.it-sce.de',
  clientId: 'organization-portal-frontend',
  apiBase: '',
  customerLabel: 'IT-SCE',
  mode: 'tenant',
  staffCockpit: false,
}

const COLOR_RE = /^#[0-9a-fA-F]{3,8}$/

let current: RuntimeConfig | null = null

function pick(raw: unknown, where: string): Partial<RuntimeConfig> {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error(`${where}: Objekt erwartet`)
  }
  const r = raw as Record<string, unknown>
  const out: Partial<RuntimeConfig> = {}
  for (const key of ['realm', 'keycloakUrl', 'clientId', 'apiBase', 'customerLabel', 'logoUrl', 'primaryColor', 'cortexUrl', 'adminApiBase', 'orgRef'] as const) {
    const v = r[key]
    if (v === undefined || v === null) continue
    if (typeof v !== 'string') throw new Error(`${where}.${key}: String erwartet`)
    out[key] = v.trim()
  }
  if (out.realm === '') delete out.realm
  if (out.primaryColor && !COLOR_RE.test(out.primaryColor)) {
    throw new Error(`${where}.primaryColor: Hex-Farbe erwartet (z. B. #FF6B35)`)
  }
  if (r.comingSoon !== undefined && r.comingSoon !== null) {
    if (!Array.isArray(r.comingSoon) || r.comingSoon.some((v) => typeof v !== 'string')) {
      throw new Error(`${where}.comingSoon: Liste von Strings erwartet`)
    }
    out.comingSoon = (r.comingSoon as string[]).map((v) => v.trim()).filter(Boolean)
  }
  if (r.staffCockpit !== undefined && r.staffCockpit !== null) {
    if (typeof r.staffCockpit !== 'boolean') throw new Error(`${where}.staffCockpit: true/false erwartet`)
    out.staffCockpit = r.staffCockpit
  }
  if (r.mode !== undefined && r.mode !== null) {
    if (!MODES.includes(r.mode as CockpitInstanceMode)) {
      throw new Error(`${where}.mode: einer von ${MODES.join(', ')}`)
    }
    out.mode = r.mode as CockpitInstanceMode
  }
  return out
}

/**
 * Pfad-Instanz (cockpit.it-sce.de/<kennung>/), fail-closed: die Datei unter /<kennung>/config.json
 * muss genau diese Instanz beschreiben. API und Cortex nur relativ unter dem Instanz-Pfad
 * (gleiches Origin, CSP connect-src), keine Host-Tabelle, keine zentralen Adressen.
 */
function assertPathInstance(file: unknown, explicit: Partial<RuntimeConfig>, tenantId: string): void {
  const where = `/${tenantId}/config.json`
  const errs: string[] = []
  if ((file as RuntimeConfigFile).hostOverrides !== undefined) errs.push('keine hostOverrides erlaubt')
  if (explicit.orgRef !== tenantId) errs.push(`orgRef muss "${tenantId}" sein`)
  if (explicit.apiBase !== `/${tenantId}`) errs.push(`apiBase muss "/${tenantId}" sein`)
  if (explicit.cortexUrl !== `/${tenantId}/cortex`) errs.push(`cortexUrl muss "/${tenantId}/cortex" sein`)
  if (explicit.adminApiBase) errs.push('adminApiBase nicht erlaubt')
  if (explicit.staffCockpit) errs.push('staffCockpit nicht erlaubt')
  if (explicit.keycloakUrl && !/^https:\/\/[^/]+$/.test(explicit.keycloakUrl.replace(/\/+$/, ''))) {
    errs.push('keycloakUrl muss ein https-Origin sein')
  }
  if (explicit.logoUrl && /^([a-z][a-z0-9+.-]*:|\/\/)/i.test(explicit.logoUrl)) {
    errs.push('logoUrl nur relativ (gleiches Origin, CSP)')
  }
  if (errs.length) throw new Error(`${where}: ${errs.join('; ')}`)
}

/** Kennung, wenn `v` ein relativer Pfad unter `/<kennung>` ist (`/<kennung>`, `/<kennung>/...`); sonst null. */
function kennungPrefix(v: string | undefined): string | null {
  const m = /^\/([^/?#]+)(?:[/?#]|$)/.exec(v ?? '')
  return m && KENNUNG_RE.test(m[1]) ? m[1] : null
}

/**
 * Merkmale, die NUR eine Pfad-Instanz-Config traegt (QM-Review 982, Auflage 1). Leere Liste = Legacy-tauglich.
 *
 * Abgrenzung (Legacy muss weiterlaufen):
 *   - `mode=tenant` ohne orgRef = Legacy-Tenant-Cockpit am geteilten Backend (hostOverrides) -> kein Merkmal.
 *   - `orgRef` allein ist KEIN Merkmal: die host-basierte Kunden-Instanz (config.glasbrenner.json, dev-mock)
 *     traegt orgRef = Tenant-Slug, apiBase "" und eine absolute cortexUrl.
 *   - Merkmal ist erst `orgRef` im Kennungs-Format (Pfad-Instanz: orgRef === Kennung, assertPathInstance)
 *     oder apiBase/cortexUrl relativ unter `/<kennung>` (Legacy: "" bzw. absolute URL; relative Pfade
 *     unter einer Kennung ergeben nur hinter dem Instanz-Pfad Sinn).
 * Jede gueltige Pfad-Instanz-Config erfuellt alle drei Merkmale (orgRef, apiBase, cortexUrl sind dort Pflicht).
 * Restrisiko: ein Legacy-Slug im Kennungs-Format wird abgelehnt -> sichtbarer Fehler, kein stiller Fallback.
 */
export function pathInstanceSignals(cfg: Partial<RuntimeConfig>): string[] {
  const out: string[] = []
  if (cfg.orgRef && KENNUNG_RE.test(cfg.orgRef)) out.push(`orgRef "${cfg.orgRef}" ist eine Kennung`)
  const api = kennungPrefix(cfg.apiBase)
  if (api) out.push(`apiBase liegt unter /${api}`)
  const cortex = kennungPrefix(cfg.cortexUrl)
  if (cortex) out.push(`cortexUrl liegt unter /${cortex}`)
  return out
}

/**
 * Legacy-Modus (<base href="/"> oder leer), fail-closed gegen fail-open (QM-Review 982, Auflage 1):
 * Fehlt COCKPIT_BASE_PATH oder greift sub_filter nicht, liefe eine Kunden-Instanz sonst als Legacy
 * (kein basename, kein Speicher-Praefix, Token in localStorage). Eine Instanz-Config wird deshalb hart
 * abgelehnt; die App startet nicht (main.tsx: Fehlerzustand, kein Keycloak-Init).
 */
function assertLegacyConfig(explicit: Partial<RuntimeConfig>, pathname: string | undefined): void {
  const signals = pathInstanceSignals(explicit)
  if (!signals.length) return
  const k = pathname ? kennungFromPath(pathname) : null
  if (k) {
    // (b) Pfad beginnt mit einer Kennung, Base ist aber "/" -> Instanz ohne Base-Pfad, nicht still Legacy.
    throw new Error(
      `Instanz-Pfad /${k}/ ohne passendes <base href> (COCKPIT_BASE_PATH fehlt oder sub_filter greift nicht): ${signals.join('; ')}`,
    )
  }
  throw new Error(`config.json: Instanz-Config im Legacy-Modus (<base href="/">) abgelehnt: ${signals.join('; ')}`)
}

/**
 * Rein: Datei + Hostname -> wirksame Config. Wirft bei ungueltigem Inhalt.
 * `tenantId` gesetzt = Pfad-Instanz (Datei kam von /<tenantId>/config.json), sonst Legacy.
 * `pathname` (nur Legacy) = Browser-Pfad fuer die Fehlermeldung bei Instanz-Pfad ohne Base.
 */
export function resolveRuntimeConfig(file: unknown, host: string, tenantId?: string, pathname?: string): RuntimeConfig {
  const base = pick(file, 'config.json')
  const overrides = (file as RuntimeConfigFile).hostOverrides
  let override: Partial<RuntimeConfig> = {}
  if (overrides !== undefined) {
    if (overrides === null || typeof overrides !== 'object' || Array.isArray(overrides)) {
      throw new Error('config.json.hostOverrides: Objekt erwartet')
    }
    if (Object.prototype.hasOwnProperty.call(overrides, host)) {
      override = pick(overrides[host], `config.json.hostOverrides["${host}"]`)
    }
  }
  const explicit = { ...base, ...override }
  if (tenantId) assertPathInstance(file, explicit, tenantId)
  else assertLegacyConfig(explicit, pathname)
  if (explicit.orgRef) {
    // Kunden-Instanz (eigenes Backend), fail-closed: alles muss AUSDRUECKLICH in der Config
    // stehen. Kein stiller Fallback auf die IT-SCE-Werte (DEFAULTS) fuer Keycloak/Client und
    // kein zentraler Lookup (realm-for-host / cortex-for-host / by-domain).
    const missing: string[] = (['realm', 'keycloakUrl', 'clientId', 'cortexUrl'] as const).filter((k) => !explicit[k])
    if (explicit.mode !== 'tenant') missing.unshift('mode=tenant')
    if (missing.length) throw new Error(`config.json: Kunden-Instanz (orgRef gesetzt) braucht ${missing.join(', ')}`)
  }
  return tenantId ? { ...DEFAULTS, ...explicit, tenantId } : { ...DEFAULTS, ...explicit }
}

/**
 * Laedt die Laufzeit-Config und macht sie ueber getRuntimeConfig() verfuegbar.
 * Legacy: /config.json. Pfad-Instanz: /<kennung>/config.json (lib/tenantPath.ts).
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const pt = currentPathTenant()
  if (pt.kind === 'error') throw new Error(pt.reason)
  const path = configJsonPath(pt)
  let res: Response
  try {
    res = await fetch(path, { cache: 'no-store' })
  } catch (e) {
    throw new Error(`${path} nicht erreichbar (${e instanceof Error ? e.message : String(e)})`)
  }
  if (!res.ok) throw new Error(`${path} nicht ladbar (HTTP ${res.status})`)
  let json: unknown
  try {
    json = await res.json()
  } catch {
    // z. B. SPA-Fallback von nginx liefert index.html, wenn die Datei fehlt
    throw new Error(`${path} ist kein gueltiges JSON`)
  }
  current = resolveRuntimeConfig(
    json,
    window.location.hostname,
    pt.kind === 'instance' ? pt.id : undefined,
    window.location.pathname,
  )
  return current
}

/** Synchroner Zugriff; erst nach loadRuntimeConfig() (main.tsx gated das Rendern). */
export function getRuntimeConfig(): RuntimeConfig {
  if (!current) throw new Error('Runtime-Config noch nicht geladen')
  return current
}

/**
 * apiBaseUrl fuer den KeycloakAuthProvider. NIE `cfg.apiBase || undefined`: der Provider legt seine
 * axios-Instanz (App: keycloakApi) nur an, wenn apiBaseUrl !== undefined. Legacy-Hosts haben
 * apiBase "" (gleicher Origin); "" || undefined = undefined liess keycloakApi null sein, jeder
 * keycloakApi.get warf einen TypeError, die abgefangenen Fehler zeigten "Keine Organisationen" bzw.
 * "Organisation nicht gefunden" (Regression 15.10.2026, TQ #690).
 */
export function authApiBaseUrl(cfg: Pick<RuntimeConfig, 'apiBase'>): string {
  return cfg.apiBase
}

export function isPersonalMode(): boolean {
  return getRuntimeConfig().mode === 'personal'
}

export function isCustomDomain(): boolean {
  return getRuntimeConfig().mode === 'tenant'
}

/** Nur fuer Tests. */
export function setRuntimeConfigForTest(cfg: RuntimeConfig | null): void {
  current = cfg
}

/** Origin der API (leer = gleicher Origin). Vor dem Config-Load: gleicher Origin. */
export function apiOrigin(): string {
  return (current?.apiBase ?? '').replace(/\/+$/, '')
}

/** Basis fuer Clients, deren Pfade OHNE /api beginnen (z. B. '/public/...'). */
export function apiPrefix(): string {
  return `${apiOrigin()}/api`
}

/**
 * Kunden-Instanz mit eigenem Backend (orgRef gesetzt): keine zentralen Pfade, Org fest aus
 * der Config. Ohne orgRef bleibt mode=tenant das Legacy-Tenant-Cockpit am geteilten Backend.
 */
export function isTenantInstance(): boolean {
  const c = getRuntimeConfig()
  return c.mode === 'tenant' && !!c.orgRef
}
